import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { ShoppingBag, FileText, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { ordersApi, Order as ApiOrder } from '../api/orders';
import { Order, CartItem } from '../context/AppContext';

function transformApiOrder(apiOrder: ApiOrder): Order {
  return {
    id: apiOrder.order_number,
    type: apiOrder.type,
    items: apiOrder.items.map(item => ({
      product: {
        id: item.product_id,
        category: 'vetements' as const,
        subcategory: 'vestes' as const,
        sector: [],
        name: `Product ${item.product_id}`,
        nameEn: `Product ${item.product_id}`,
        brand: '',
        ref: '',
        price: item.unit_price,
        currency: 'EUR',
        stock: 0,
        moq: 1,
        quoteThreshold: 0,
        norm: [],
        description: '',
        descriptionEn: '',
        images: [],
        variants: {},
        features: [],
        featuresEn: [],
      },
      qty: item.quantity,
    })) as CartItem[],
    date: apiOrder.created_at,
    status: apiOrder.status,
    form: apiOrder.form_data || {},
  };
}

export default function OrdersPage() {
  usePageTitle("Mes commandes — Lodiss");

  const { lang, orders: localOrders, setPage, currentUser } = useApp();
  const T = t[lang];
  const [orders, setOrders] = useState<Order[]>(localOrders);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setOrders(localOrders);
      return;
    }

    setLoading(true);
    ordersApi.getOrders()
      .then(apiOrders => {
        setOrders(apiOrders.map(transformApiOrder));
      })
      .catch(() => {
        setOrders(localOrders);
      })
      .finally(() => setLoading(false));
  }, [currentUser, localOrders]);

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h1 className="text-2xl font-bold text-gray-950 mb-6">{T.common.myOrders}</h1>

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <ShoppingBag size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm mb-5">{T.common.noOrders}</p>
            <button
              onClick={() => setPage('catalogue')}
              className="px-5 py-2.5 bg-lodiss-red hover:bg-lodiss-dark text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {T.cart.goCatalogue}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    {order.type === 'direct' ? (
                      <ShoppingBag size={16} className="text-lodiss-red" />
                    ) : (
                      <FileText size={16} className="text-amber-500" />
                    )}
                    <span className="text-sm font-bold text-gray-900 font-mono">{order.id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      order.type === 'direct'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
                  <Clock size={12} />
                  {new Date(order.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>

                <ul className="space-y-2 mb-4">
                  {order.items.map(item => (
                    <li key={item.product.id} className="flex items-center gap-3 text-sm">
                      <img
                        src={item.product.images[0]}
                        alt=""
                        className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                      />
                      <span className="flex-1 text-gray-700 truncate">
                        {lang === 'fr' ? item.product.name : item.product.nameEn}
                      </span>
                      <span className="text-gray-500 font-medium flex-shrink-0">×{item.qty}</span>
                      {order.type === 'direct' ? (
                        <span className="text-gray-900 font-semibold flex-shrink-0">
                          {(Number(item.product.price) * item.qty).toFixed(3)} TND
                        </span>
                      ) : (
                        <span className="text-amber-600 text-xs font-semibold flex-shrink-0">
                          {lang === 'fr' ? 'Sur devis' : 'On quote'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                {order.type === 'direct' && (
                  <div className="pt-3 border-t border-gray-100 flex justify-between text-sm">
                    <span className="text-gray-500">{T.cart.total}</span>
                    <span className="font-bold text-gray-950">
                      {order.items.reduce((s, i) => s + Number(i.product.price) * i.qty, 0).toFixed(3)} TND {T.common.ht}
                    </span>
                  </div>
                )}
                {order.type === 'quote' && (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                      {lang === 'fr' ? 'Réponse attendue sous 24h par notre équipe commerciale.' : 'Response expected within 24h from our sales team.'}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
